import React, { FC, useContext } from 'react';
import { TranslationsProvider } from '@eo-locale/react';
import plTranslations from './translations/pl.json';

import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { AgreementsProvider } from './components/organisms/AgreementsProvider';
import { ToastProvider } from './components/atoms/Toast';
import { AppVersionBadge } from './components/atoms/AppVersionBadge';

import { RoutePath } from './config';
import {
  HomePage,
  LoginPage,
  RegisterPage,
  RegisterSuccessPage,
  PersonalDataPage,
  MembershipPage,
  ContributionsPage,
  CardPage,
  CardStatusPage,
  ContactPage,
  CheckoutPage,
  PaymentResultPage,
  PaymentSuccessfulPage,
  PaymentFailedPage,
  // HistoryPage,

} from './pages';
import { Agreements } from './pages/Agreements/Agreements';
import { PermissionsPage } from './pages/PermissionsPage';
import { DistinctionsPage } from './pages/DistinctionsPage';
import { FunctionsPage } from './pages/FunctionsPage';
import { useAutoReloadOnNewVersion } from './hooks/useAutoReloadOnNewVersion';
import { useAuthHeartbeat } from './hooks/useAuthHeartbeat';
import { AuthContextProvider, ProfileLayout, useAuthContext } from './components';
import { GlobalCardTypeGate } from './components/organisms/GlobalCardTypeGate';


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnMount: 'always',
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: 1,
      cacheTime: 0,
      staleTime: 0,
    },
    mutations: {
      retry: 0,
    },
  },
});

const locales = [
  { language: 'pl', messages: plTranslations },
];

const GlobalWatchers: React.FC = () => {
  const { isAuthorized } = useAuthContext();
  const { pathname } = useLocation();

  const isPublic = pathname.startsWith('/card-status');
  const watchersEnabled = isAuthorized === true && !isPublic;

  useAutoReloadOnNewVersion({
    enabled: watchersEnabled,
    intervalMs: 30000,
    ignoreDuringPaths: ['/checkout', '/payment'],
  });

  useAuthHeartbeat({
    enabled: watchersEnabled,
    intervalMs: 15000,
    pingPath: '/ping',
  });

  return null;
};


const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthorized } = useAuthContext();
  const location = useLocation();

  if (isAuthorized === undefined) {
    return null;
  }
  if (!isAuthorized) {
    return <Navigate to="/login" replace state={{ next: location.pathname + location.search }} />;
  }
  return <>{children}</>;
};

export const App: FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TranslationsProvider language="pl" locales={locales}>
        <BrowserRouter>
          <AuthContextProvider>

            <GlobalWatchers />
            <GlobalCardTypeGate />
            <AppVersionBadge />
            <ToastProvider>
              <AgreementsProvider>
                <Routes>
                  <Route path={RoutePath.Home} element={<HomePage />} />
                  <Route path={RoutePath.Login} element={<LoginPage />} />
                  <Route path={RoutePath.Register} element={<RegisterPage />} />
                  <Route path={RoutePath.RegisterSuccess} element={<RegisterSuccessPage />} />

                  <Route path={RoutePath.Profile} element={<RequireAuth><ProfileLayout /></RequireAuth>}>
                    <Route index element={<Navigate replace to={RoutePath.PersonalData} />} />
                    <Route path={RoutePath.PersonalData} element={<RequireAuth><PersonalDataPage /></RequireAuth>} />
                    <Route path={RoutePath.Membership} element={<RequireAuth><MembershipPage /></RequireAuth>} />
                    <Route path={RoutePath.Contributions} element={<RequireAuth><ContributionsPage /></RequireAuth>} />
                    <Route path={RoutePath.CurrentFunctions} element={<RequireAuth><FunctionsPage /></RequireAuth>} />
                    <Route path={RoutePath.Distictions} element={<RequireAuth><DistinctionsPage /></RequireAuth>} />
                    <Route path={RoutePath.Permissions} element={<RequireAuth><PermissionsPage /></RequireAuth>} />
                    <Route path={RoutePath.Agreements} element={<RequireAuth><Agreements /></RequireAuth>} />
                    <Route path="*" element={<Navigate replace to={RoutePath.PersonalData} />} />
                  </Route>

                  <Route path={RoutePath.Card} element={<RequireAuth><CardPage /></RequireAuth>} />
                  <Route path={RoutePath.CardStatus} element={<CardStatusPage />} />
                  <Route path={RoutePath.Contact} element={<RequireAuth><ContactPage /></RequireAuth>} />

                  <Route path={RoutePath.Checkout} element={<RequireAuth><CheckoutPage /></RequireAuth>} />
                  <Route path={RoutePath.PaymentResult} element={<PaymentResultPage />} />
                  <Route path={RoutePath.PaymentSuccessful}
                         element={<RequireAuth><PaymentSuccessfulPage /></RequireAuth>} />
                  <Route path={RoutePath.PaymentFailed} element={<RequireAuth><PaymentFailedPage /></RequireAuth>} />

                  <Route path="*" element={<Navigate replace to={RoutePath.Home} />} />
                </Routes>
              </AgreementsProvider>
            </ToastProvider>
          </AuthContextProvider>
        </BrowserRouter>
      </TranslationsProvider>
    </QueryClientProvider>
  );
};